import type { LimitsEnrichItem } from "../api/limitsEnrichItem";
import {
  CHECK_TYPE_KEYS,
  CHECK_TYPE_LABELS,
  type CheckTypeKey,
} from "../api/pricelist";
import type { UserInfo, UserLimits } from "../api/user";
import { getCurrentActiveTariff } from "./tariffTimeline";

export interface TopUpRow {
  checkType: CheckTypeKey;
  label: string;
  enrichItemId: string;
  unitAmount: number;
  unitPrice: number;
}

export const isPeriodActive = (
  userLimits?: UserLimits | null,
  now = new Date()
): boolean => {
  if (!userLimits) return false;
  if (typeof userLimits.active === "boolean") return userLimits.active;
  if (!userLimits.periodEnd) return false;
  const end = Date.parse(userLimits.periodEnd);
  if (!Number.isFinite(end)) return false;
  return end > now.getTime();
};

export const hasActivePeriod = (userInfo?: UserInfo | null, now = new Date()): boolean => {
  if (!userInfo) return false;
  if (userInfo.userLimits != null) {
    return isPeriodActive(userInfo.userLimits, now);
  }
  return Boolean(getCurrentActiveTariff(userInfo.activeTariffs ?? [], now));
};

export const pickTopUpItems = (items: LimitsEnrichItem[]): TopUpRow[] => {
  const active = items.filter(
    (item) =>
      item.active !== false &&
      item.id &&
      Number.isFinite(item.amount) &&
      item.amount > 0 &&
      Number.isFinite(item.price) &&
      item.price >= 0
  );

  return CHECK_TYPE_KEYS.flatMap((checkType) => {
    const candidates = active
      .filter((item) => item.checkType === checkType)
      .sort((a, b) => a.amount - b.amount || a.price - b.price);
    const item = candidates[0];
    if (!item?.id) return [];

    return [
      {
        checkType,
        label: CHECK_TYPE_LABELS[checkType],
        enrichItemId: item.id,
        unitAmount: item.amount,
        unitPrice: item.price,
      },
    ];
  });
};

export const normalizeTopUpPacks = (packs: number): number => {
  if (!Number.isFinite(packs)) return 1;
  return Math.max(1, Math.floor(packs));
};

export const topUpTotals = (row: TopUpRow, packs: number) => {
  const qty = normalizeTopUpPacks(packs);
  return {
    packs: qty,
    count: row.unitAmount * qty,
    price: row.unitPrice * qty,
  };
};

export const validateTopUpRow = (row: TopUpRow, packs = 1): string | null => {
  if (!row.enrichItemId) return "Пакет докупки недоступен";
  if (!Number.isFinite(row.unitAmount) || row.unitAmount <= 0) {
    return "Некорректный размер пакета";
  }
  if (!Number.isFinite(row.unitPrice) || row.unitPrice < 0) {
    return "Некорректная цена пакета";
  }
  const { count } = topUpTotals(row, packs);
  if (count % row.unitAmount !== 0) {
    return "Количество должно быть кратно пакету";
  }
  return null;
};
